// Bull Run's frame (sprint 35 item 2), drawn only from the Aseprite exports in client/art/aseprite/bull-run/
// (art/aseprite/src/bull_run.lua). This file chooses which frame of which sheet goes where on each 1/15 s step; it draws
// no shape of its own. bullScene() is pure (tested in test/bull-run-art.test.js); drawBull() copies its list onto the canvas.
//
// Reading the game's observation (game/minigames/bull.js observe(): t, lean, vel, events):
//  - the tell (5 steps before a buck): the head drops low on the buck's side, clear of the rider, and the nostril on that
//    side snorts a big paper puff;
//    the big rear's tell keeps the head straight and paws (run frames) with a double snort; step 5's lone snort teaches it;
//  - a buck: buck-left / buck-right for 3 steps, then land (snow puffs at the hooves); the big rear: 5 steps of rear;
//  - otherwise the charge: run-a / run-b, two steps each, while the street and the lamps slide down past the bull.
// The eight lamp posts are the clock: lamp n reaches the bull's hooves at step 15n, so the eighth arrives with the bell.
// Reduced motion: the street is a still frame and the lamps stand still; the bull's and rider's frames still step (they are the game).
import { sheet, blit, frameOf } from './minigame-art.js';

const PIECE = 'bull-run';
export const ART = {
  street: sheet(PIECE, 'bull-run-street'),
  cone: sheet(PIECE, 'bull-run-cone', { still: true }),
  lamp: sheet(PIECE, 'bull-run-lamp'),
  bell: sheet(PIECE, 'bull-run-bell'),
  bull: sheet(PIECE, 'bull-run-bull'),
  front: sheet(PIECE, 'bull-run-bull-front'),
  rider: sheet(PIECE, 'bull-run-rider'),
  snort: sheet(PIECE, 'bull-run-snort'),
  snow: sheet(PIECE, 'bull-run-snow'),
  leanbar: sheet(PIECE, 'bull-run-leanbar', { still: true }),
  marker: sheet(PIECE, 'bull-run-marker', { still: true }),
  pips: sheet(PIECE, 'bull-run-pips'),
  uponly: sheet(PIECE, 'bull-run-uponly'),
};

// Layout on the 180 x 300 frame (logical px). The plate's text covers the top rows, so the HUD starts at row 41.
export const LAYOUT = { bull: [58, 158], vy: 72, h: 150, kerb: [4, 0.4], dz: 0.1, pips: [44, 41, 12], bar: [26, 51], uponly: [21, 84], ground: 236 };
export const STEPS = 120, LAMPS = 8, LAMP_EVERY = 15, TELL = 5;
// Anchors written by bull_run.lua (bull-run-anchors.json); these are the values it writes, checked by the test.
export const ANCHORS = {
  bull: {
    stand: { nostrilL: [29, 38], nostrilR: [35, 38], saddle: [32, 13], hooves: [[23, 62], [41, 62]] },
    'drop-left': { nostrilL: [24, 55], nostrilR: [31, 54], saddle: [33, 14], hooves: [[21, 62], [43, 62]] },
    'drop-right': { nostrilL: [33, 54], nostrilR: [40, 55], saddle: [31, 14], hooves: [[21, 62], [43, 62]] },
    'buck-left': { nostrilL: [28, 42], nostrilR: [34, 39], saddle: [27, 12], hooves: [[18, 64], [44, 60]] },
    'buck-right': { nostrilL: [30, 39], nostrilR: [36, 42], saddle: [37, 12], hooves: [[46, 64], [20, 60]] },
    rear: { nostrilL: [29, 30], nostrilR: [35, 30], saddle: [32, 6], hooves: [[19, 62], [45, 62]] },
    land: { nostrilL: [29, 41], nostrilR: [35, 41], saddle: [32, 16], hooves: [[17, 62], [47, 62]] },
    'run-a': { nostrilL: [29, 37], nostrilR: [35, 37], saddle: [32, 12], hooves: [[24, 62], [41, 62]] },
    'run-b': { nostrilL: [29, 39], nostrilR: [35, 39], saddle: [32, 14], hooves: [[23, 62], [40, 62]] },
  },
  lamp: { near: { foot: [6, 45], bell: [11, 9] }, far: { foot: [9, 45], bell: [12, 27] }, w: 28 },
  rider: { seat: [10, 15] },
  snort: { size: [14, 8] },
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const kerbAt = y => LAYOUT.kerb[0] + (y - LAYOUT.vy) * LAYOUT.kerb[1];

// What the bull does on step t (and `after` steps past the end). Returns { pose, puffs: frame or -1, snow: frame or -1 }.
export function bullPose(o, after = 0) {
  const t = o.t, thrown = Math.abs(o.lean) >= 1000, win = !thrown && t >= STEPS;
  if (win) return { pose: after < 1 ? 'land' : 'stand', puffs: -1, snow: after < 2 ? after : -1 };
  if (thrown) return { pose: (t + after) >> 1 & 1 ? 'run-b' : 'run-a', puffs: -1, snow: -1 };
  // the kick itself first; then a tell (it may start while the last buck settles: the tell always shows in full); then the buck's tail and the landing
  const buckOf = e => ({ pose: !e.sideShown ? 'rear' : e.side < 0 ? 'buck-left' : 'buck-right', puffs: -1, snow: -1 });
  const kick = o.events.find(e => t === e.buckStep);
  if (kick) return buckOf(kick);
  const tell = o.events.find(e => t >= e.tellStep && t < e.buckStep);
  if (tell) {
    const puffs = Math.min(2, (t - tell.tellStep) >> 1);
    return !tell.sideShown ? { pose: t & 1 ? 'run-b' : 'run-a', puffs, snow: -1 } : { pose: tell.side < 0 ? 'drop-left' : 'drop-right', puffs, snow: -1 };
  }
  for (const e of o.events) {
    const len = e.sideShown ? 3 : 5;
    if (t > e.buckStep && t < e.buckStep + len) return buckOf(e);
    if (t >= e.buckStep + len && t < e.buckStep + len + 2) return { pose: 'land', puffs: -1, snow: t - e.buckStep - len };
  }
  if (t >= 5 && t < 5 + TELL) return { pose: 'stand', puffs: Math.min(2, (t - 5) >> 1), snow: -1 }; // the first snort, no buck: it teaches the tell
  return { pose: t >> 1 & 1 ? 'run-b' : 'run-a', puffs: -1, snow: -1 };
}

// The rider's frame: the counter-lean shows as slipping once the lean passes 0.45; the hand (and hat) go up at the bell.
export function riderPose(o, after = 0) {
  const thrown = Math.abs(o.lean) >= 1000;
  if (thrown) return after < 3 ? 'thrown-1' : after < 6 ? 'thrown-2' : 'thrown-3';
  if (o.t >= STEPS && after >= 1) return 'hand-up';
  return o.lean <= -450 ? 'slip-left' : o.lean >= 450 ? 'slip-right' : 'centred';
}

// The lamps on the street at scene step s: depth z = 1 at the bull's hooves (row 222). Under reduced motion they stand still.
export function lamps(s, reduced) {
  const out = [];
  const zs = reduced ? [1, 2.5, 4, 5.5].map((z, i) => ({ n: LAMPS - i, z })) : Array.from({ length: LAMPS }, (_, i) => ({ n: i + 1, z: 1 + (LAMP_EVERY * (i + 1) - s) * LAYOUT.dz }));
  for (const { n, z } of zs) {
    if (z < 0.55 || z > 9) continue;
    const y = Math.round(LAYOUT.vy + LAYOUT.h / z), hw = kerbAt(y);
    if (y > 300 + 48) continue;
    out.push({ n, z, y, near: z < 2, left: Math.round(90 - hw - 5), right: Math.round(90 + hw + 5) });
  }
  return out.sort((a, b) => b.z - a.z);
}

// The whole frame as a list of copies: { art, frame, x, y } (plus sw for a crop), bottom first.
export function bullScene(o, { reduced = false, after = 0 } = {}) {
  const ops = [], put = (art, frame, x, y, sw) => ops.push({ art, frame, x: Math.round(x), y: Math.round(y), ...(sw !== undefined ? { sw } : {}) });
  const thrown = Math.abs(o.lean) >= 1000, win = !thrown && o.t >= STEPS;
  const scene = reduced ? 0 : win ? STEPS : o.t + (thrown ? after : 0);
  put('street', ['run', reduced ? 0 : scene % LAMP_EVERY], 0, 0);
  put('cone', 0, 0, 0);
  for (const l of lamps(scene, reduced)) {
    const a = l.near ? ANCHORS.lamp.near : ANCHORS.lamp.far, kind = l.near ? 'near' : 'far';
    const lx = l.left - a.foot[0], ly = l.y - a.foot[1];
    put('lamp', [`${kind}-left`, 0], lx, ly);
    put('lamp', [`${kind}-right`, 0], l.right - (ANCHORS.lamp.w - 1 - a.foot[0]), ly);
    if (l.n === LAMPS) put('bell', [win && after % 4 >= 2 ? 'ringing' : 'still', 0], lx + a.bell[0] - 3, ly + a.bell[1] + 1);
  }
  const p = bullPose(o, after), A = ANCHORS.bull[p.pose];
  const [bx, by0] = LAYOUT.bull, by = by0 - (thrown && !reduced ? Math.min(after, 14) * 6 : 0);
  put('bull', [p.pose, 0], bx, by);
  const rp = riderPose(o, after), seat = ANCHORS.rider.seat;
  if (!thrown) {
    put('rider', [rp, 0], bx + A.saddle[0] - seat[0] + Math.round((clamp(o.lean, -999, 999) * 12) / 1000), by + A.saddle[1] - seat[1]);
    put('front', [p.pose, 0], bx, by);
  }
  if (p.puffs >= 0) {
    // 14 x 8 puffs, their inner end on the nostril. A dropped head snorts only from the nostril on its drop side (the side
    // the buck goes), so the side reads at a glance; the lone teaching snort and the big rear's tell snort from both.
    const W = ANCHORS.snort.size[0], H = ANCHORS.snort.size[1];
    if (p.pose !== 'drop-right') put('snort', ['puff-left', p.puffs], bx + A.nostrilL[0] - W + 2, by + A.nostrilL[1] - (H >> 1));
    if (p.pose !== 'drop-left') put('snort', ['puff-right', p.puffs], bx + A.nostrilR[0] - 1, by + A.nostrilR[1] - (H >> 1));
  }
  if (p.snow >= 0) for (const h of A.hooves) put('snow', ['land', p.snow], bx + h[0] - 6, by + Math.min(62, h[1]) - 3);
  if (thrown) {
    // the 3-frame arc: up and out over the side it fell, then flat in the snow with a puff
    const side = o.lean < 0 ? -1 : 1, s0 = ANCHORS.bull.stand.saddle, k = Math.min(after, 6);
    const x = bx + s0[0] - seat[0] + side * (14 + k * 7), y = Math.min(LAYOUT.ground - 17, by0 + s0[1] - seat[1] - 10 * k + 3 * k * k);
    put('rider', [rp, 0], x, k >= 6 ? LAYOUT.ground - 17 : y);
    if (after >= 6 && after < 10) put('snow', ['land', after < 8 ? 0 : 1], x + 4, LAYOUT.ground);
  }
  // the HUD: eight lamp pips (one lights a second), the lean bar (its tall ticks at +-0.7, the clean-ride line), the marker at the lean
  const lit = Math.min(LAMPS, Math.floor(o.t / LAMP_EVERY));
  for (let i = 0; i < LAMPS; i++) put('pips', [i < lit ? 'lit' : 'unlit', 0], LAYOUT.pips[0] + i * LAYOUT.pips[2], LAYOUT.pips[1]);
  put('leanbar', 0, LAYOUT.bar[0], LAYOUT.bar[1]);
  put('marker', 0, LAYOUT.bar[0] + 64 + Math.round((clamp(o.lean, -1000, 1000) * 60) / 1000) - 2, LAYOUT.bar[1] - 1);
  if (win) put('uponly', [after < 1 ? 'stamp-in' : 'settle', 0], LAYOUT.uponly[0], LAYOUT.uponly[1]);
  return ops;
}

export function drawBull(g, o, { reduced = false, after = 0 } = {}) {
  g.clearRect?.(0, 0, 180, 300);
  const ops = bullScene(o, { reduced, after });
  for (const op of ops) {
    const s = ART[op.art];
    const i = Array.isArray(op.frame) ? frameOf(s, op.frame[0], op.frame[1]) : op.frame;
    blit(g, s, i, op.x, op.y, op.sw);
  }
  return ops; // what was copied where (the page keeps it for its own checks)
}
